import {
  Children,
  Fragment,
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import schema from "./template-pages-schema.json";
import { getAppTemplate, IS_DEMO, type CustomNavigation } from "./api";
export type TemplatePageConfig = {
  id: string;
  name: string;
  title: string;
  layout: string;
  enabled: boolean;
  navigation: boolean;
  sort: number;
  columns: number;
  gap: number;
  blocks: { id: string; enabled: boolean; sort: number }[];
};
export type AppTemplateConfig = {
  id: string;
  code: string;
  rendererVersion: number;
  theme: { accent: string; surface: string };
  pages: TemplatePageConfig[];
};
export function normalizeTemplate(value: unknown): AppTemplateConfig {
  const raw = value as Partial<AppTemplateConfig> | null;
  const color = (key: "accent" | "surface") =>
    /^#[a-f0-9]{6}$/i.test(raw?.theme?.[key] || "")
      ? raw!.theme![key]
      : schema.theme[key];
  const safeText = (value: unknown, fallback: string) =>
    typeof value === "string" && value.trim()
      ? value.trim().slice(0, 80)
      : fallback;
  const sort = (value: unknown, fallback: number) =>
    Number.isFinite(Number(value))
      ? Math.max(1, Math.floor(Number(value)))
      : fallback;
  return {
    id: safeText(raw?.id, "TPL-DEFAULT"),
    code: safeText(raw?.code, "default"),
    rendererVersion: 1,
    theme: { accent: color("accent"), surface: color("surface") },
    pages: schema.pages
      .map((base) => {
        const p = Array.isArray(raw?.pages)
          ? raw.pages.find((p) => p?.id === base.id)
          : undefined;
        return {
          id: base.id,
          name: safeText(p?.name, base.name),
          title: safeText(p?.title, base.title),
          layout: base.layouts.includes(p?.layout || "")
            ? p!.layout
            : base.layouts[0],
          enabled: base.id === "home" || p?.enabled !== false,
          navigation: base.navigation && p?.navigation !== false,
          sort: sort(p?.sort, base.sort),
          columns: [1, 2, 3].includes(p?.columns || 0)
            ? p!.columns
            : base.columns,
          gap: [8, 12, 16, 20].includes(p?.gap || 0) ? p!.gap : base.gap,
          blocks: base.blocks
            .map((b) => {
              const block = Array.isArray(p?.blocks)
                ? p.blocks.find((block) => block?.id === b.id)
                : undefined;
              return {
                id: b.id,
                enabled: b.locked || block?.enabled !== false,
                sort: sort(block?.sort, b.sort),
              };
            })
            .sort((a, b) => a.sort - b.sort),
        };
      })
      .sort((a, b) => a.sort - b.sort),
  };
}
export const defaultTemplate = normalizeTemplate(null);
export function useTemplateConfig() {
  const [preview] = useState(() => {
    try {
      const data = new URLSearchParams(location.hash.slice(1)).get(
        "layoutPreview",
      );
      return data && data.length < 40000 ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  });
  const [template, setTemplate] = useState<AppTemplateConfig>(() =>
    normalizeTemplate(preview),
  );
  const [navigation, setNavigation] = useState<CustomNavigation | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (IS_DEMO) return;
    const controller = new AbortController();
    getAppTemplate(controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        if (!result.template) throw new Error("模板不存在或已停用");
        const config = normalizeTemplate(result.template);
        setTemplate(
          preview
            ? { ...config, ...normalizeTemplate({ ...config, ...preview }) }
            : config,
        );
        const groups = Array.isArray(result.items) ? result.items : [];
        setNavigation({
          id: config.id,
          name: groups.length === 1 ? groups[0].name : "模板导航",
          items: groups.flatMap((group) =>
            Array.isArray(group.items)
              ? group.items.map((item) => ({
                  ...item,
                  id: `${group.id}:${item.id}`,
                  categories: Array.isArray(item.categories)
                    ? item.categories
                    : [],
                  keywords: Array.isArray(item.keywords) ? item.keywords : [],
                  icon: typeof item.icon === "string" ? item.icon : "",
                }))
              : [],
          ),
        });
      })
      .catch((e) => {
        if (!controller.signal.aborted)
          setError(e instanceof Error ? e.message : "模板读取失败");
      });
    return () => controller.abort();
  }, [preview]);
  return { template, navigation, error, preview: !!preview };
}
const TemplateContext = createContext(defaultTemplate);
export const TemplateProvider = TemplateContext.Provider;
export function TemplateBlock({
  children,
}: {
  id: string;
  children: ReactNode;
}) {
  return <>{children}</>;
}
type LayoutProps = {
  page: TemplatePageConfig;
  className: string;
  children: ReactNode;
};
function StackLayout({ page, className, children }: LayoutProps) {
  return (
    <section
      className={`${className} template-page template-${page.layout}`}
      data-template-page={page.id}
      data-template-layout={page.layout}
      data-hidden-blocks={
        page.blocks
          .filter((block) => !block.enabled)
          .map((block) => block.id)
          .join(" ") || undefined
      }
      aria-label={page.title}
      style={
        {
          "--template-columns": page.columns,
          "--template-gap": `${page.gap}px`,
        } as CSSProperties
      }
    >
      {children}
    </section>
  );
}
// Layout registry holds code-owned renderers. Configuration only chooses a
// known renderer and known blocks, never executes supplied HTML or scripts.
const renderers: Record<string, typeof StackLayout> = Object.fromEntries(
  schema.pages.flatMap((page) =>
    page.layouts.map((layout) => [layout, StackLayout]),
  ),
);
export function TemplatePage({
  id,
  className,
  children,
  forceFeed = false,
}: {
  id: string;
  className: string;
  children: ReactNode;
  forceFeed?: boolean;
}) {
  const template = useContext(TemplateContext);
  const configured =
    template.pages.find((p) => p.id === id) ||
    defaultTemplate.pages.find((p) => p.id === id)!;
  const page =
    forceFeed && id === "home"
      ? { ...configured, layout: "video-feed" }
      : configured;
  const slots = new Map<string, ReactNode>();
  Children.forEach(children, (child) => {
    if (isValidElement<{ id?: string }>(child) && child.props.id)
      slots.set(child.props.id, child);
  });
  const Renderer = renderers[page.layout] || StackLayout;
  return (
    <Renderer page={page} className={className}>
      {page.blocks
        .filter(
          (block) =>
            block.enabled &&
            (id !== "home" ||
              (page.layout === "video-grid"
                ? ["header", "sorting", "catalog"].includes(block.id)
                : block.id !== "catalog")),
        )
        .map((block) => (
          <Fragment key={block.id}>{slots.get(block.id)}</Fragment>
        ))}
    </Renderer>
  );
}
export function TemplateFeedBlocks({ children }: { children: ReactNode }) {
  const page = useContext(TemplateContext).pages.find((p) => p.id === "home")!;
  const slots = new Map<string, ReactNode>();
  Children.forEach(children, (child) => {
    if (isValidElement<{ id?: string }>(child) && child.props.id)
      slots.set(child.props.id, child);
  });
  return (
    <>
      {page.blocks
        .filter((block) => block.enabled)
        .map((block) => (
          <Fragment key={block.id}>{slots.get(block.id)}</Fragment>
        ))}
    </>
  );
}
